<template>
    <div class="block-editor ai-bot-response payload" v-if="clone">
        <div class="ready-templates">
            <span class="btnImport" v-if="!showTemplates" @click="showTemplates = true"
                >{{ $t("message.importFromReadyTemplates") }}</span
            >
            <a-select
                v-else
                style="width: 300px"
                :placeholder="$t('message.selectTemplate')"
                @change="injectTemplate"
                :autofocus="true"
                :default-open="true"
            >
                <a-select-option :value="template.slug" v-for="template in templates" :key="template.slug">
                    {{ selectedLanguage === 'tr' ? template.name.tr : template.name.en }}
                </a-select-option>
            </a-select>
        </div>
        <div class="says">
            <TiptapEditor
                :show-emoji="true"
                :show-attributes="true"
                :placeholder="$t('message.botScenerio')"
                :max-character-count="6200"
                v-model="clone.prompt"
                :key="'tiptap-prompt' + renderKey"
            />
        </div>
        <div>
            <span>{{ $t("chatbotv2.saveAnswerToAttribute") }}</span>
            <a-switch
                v-model="clone.saveAsAttribute"
                :checked-children="$t('message.yes')"
                :un-checked-children="$t('message.no')"
                :default-checked="false"
            >
                {{ $t("chatbotv2.saveAnswerToAttribute") }}
            </a-switch>
        </div>

        <div class="attribute-picker-section" v-if="clone.saveAsAttribute">
            <span class="sectionTitle"> {{ $t("chatbotv2.saveResposeToAttribute") }}</span>
            <a-popover v-model="attributePickerVisible" trigger="click" :overlayClassName="'no-padding'">
                <div slot="content">
                    <AttributePicker :default-selected="clone.attribute" :pick="onAttributePicked" />
                </div>
                <div class="attribute-input">
                    <div class="attribute-input-text-wrapper">
                        <div class="attribute-input-placeholder" v-if="$_.isEmpty(clone.attribute)">
                            {{ $t("chatbotv2.chooseAttributeType") }}
                        </div>
                        <div class="single-attribute-style" v-else :class="{ active: attributePickerVisible }">
                            <AttributeIconPreview :attribute="clone.attribute" />
                            <span>
                                {{
                                    `${clone.attribute}`.startsWith("akinon_") ||
                                    `${clone.attribute}`.startsWith("probel_") ||
                                    Attributes().includes(clone.attribute)
                                        ? $t(`chatbotv2.attr.${clone.attribute}`)
                                        : clone.attribute.startsWith("contact_property_")
                                        ? visitorContactAttributes[clone.attribute.replace("contact_property_", "")]
                                        : clone.attribute
                                }}
                            </span>
                        </div>
                    </div>
                    <span class="material-icons" v-if="!$_.isEmpty(clone.attribute)"> expand_more </span>
                </div>
            </a-popover>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../common/TiptapEditor";
import CHATBOT_AI_TEMPLATES from "../../templates/chatbot-ai-templates";
import log2 from "../../../../../../utils/log2";
import AttributePicker from "../pickers/AttributePicker.vue";
import AttributeIconPreview from "../common/AttributeIconPreview.vue";
import { Attributes } from "../../../palatteEnums";
import { mapState } from "vuex";

const log = log2("AIBotResponse");
export default {
    name: "AIBotResponse",
    components: { AttributeIconPreview, AttributePicker, TiptapEditor },
    props: {
        value: {
            type: Object,
            default: () => ({
                prompt: "",
            }),
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            showTemplates: false,
            templates: CHATBOT_AI_TEMPLATES,
            renderKey: 0,
            attributePickerVisible: false,
            selectedLanguage: this.$i18n.locale
        };
    },
    computed: {
        ...mapState({
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((a, b) => {
                    a[b._id] = b.label;
                    return a;
                }, {}),
        }),
    },
    watch: {
        clone: {
            handler(newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
    },
    methods: {
        Attributes() {
            return Attributes;
        },
        onAttributePicked(attribute) {
            log("onAttributePicked", attribute);
            this.clone.attribute = attribute;
            this.attributePickerVisible = false;
        },
        injectTemplate(slug) {
            const template = this.templates.find((t) => t.slug === slug);
            log("injectTemplate", slug, template);
            if (template) {
                this.clone = {
                    ...this.clone,
                    prompt: this.selectedLanguage === 'tr' ? template.aiPrompt.tr : template.aiPrompt.en,
                };
                this.renderKey += 1;
                this.$nextTick();
            }
        },
    },
};
</script>

<style lang="scss">
.block-editor.ai-bot-response {
    padding: 30px 30px 90px;
    .attribute-picker-section {
        margin-top: 20px;

        .sectionTitle {
            margin-bottom: 10px;
            margin-top: 15px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .attribute-input {
            align-items: center;
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            height: 43px;
            padding: 15px;
            position: relative;
            transition: all 0.3s;
            user-select: none;
            width: 100%;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .attribute-input-text-wrapper {
                display: flex;
                flex-grow: 1;
                width: 0;
            }

            .attribute-input-placeholder {
                color: #9ba6b3;
                font-size: 15px;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .single-attribute-style {
                background: #ffdc66;
                cursor: pointer;
                transition: all 0.3s;
                border-radius: 10px;
                box-decoration-break: clone;
                padding: 0 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: 800;

                &.active {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #647283;
                    font-size: 18px;
                    margin-right: 3px;
                }
            }
        }
    }

    .ready-templates {
        display: flex;
        justify-content: flex-end;

        .btnImport {
            color: #006cff;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;
            cursor: pointer;
            user-select: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .ai-bot-response-header {
        align-items: center;
        display: flex;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: 16px;

        .material-icons {
            margin-right: 6px;
        }
    }

    .editable-editor-wrapper {
        border: 1px solid white;
        border-radius: 13px;
        //max-height: 50px;

        .editable-editor {
            //max-height: 50px;

            .ProseMirror {
                //max-height: 50px;
                overflow-y: auto;
            }
        }

        &.focused {
            border-color: #006cff;
        }

        &:hover {
            border-color: #006cff;
        }
    }
}
</style>
